import { ReactNode } from 'react';
import { Drawer, Box, Typography, IconButton, Stack, useMediaQuery, useTheme, CircularProgress, Button } from '@mui/material';
import { Close } from '@mui/icons-material';
import { DISPATCH, bookFieldSx } from './dispatchTheme';

interface DispatchBookShellProps {
  open: boolean;
  title: string;
  subtitle: string;
  accent: string;
  icon: ReactNode;
  onClose: () => void;
  onSubmit?: () => void;
  submitLabel?: string;
  loading?: boolean;
  disableSubmit?: boolean;
  children: ReactNode;
}

export default function DispatchBookShell({
  open, title, subtitle, accent, icon, onClose, onSubmit, submitLabel = 'Confirm',
  loading, disableSubmit, children,
}: DispatchBookShellProps) {
  const theme = useTheme();
  const compact = useMediaQuery(theme.breakpoints.down('md'));
  /** Compact density so 100% browser zoom matches the previous ~70% zoom layout. */
  const density = 0.82;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          // Full viewport at 100% zoom; size compensated for CSS zoom so content fills the screen.
          width: compact ? '100vw' : `${100 / density}vw`,
          maxWidth: compact ? '100vw' : `${100 / density}vw`,
          height: compact ? '100%' : `${100 / density}vh`,
          maxHeight: compact ? '100%' : `${100 / density}vh`,
          zoom: compact ? 1 : density,
          // Firefox has no CSS zoom — scale + origin keeps the same density.
          '@supports not (zoom: 1)': compact ? undefined : {
            zoom: 'unset',
            transform: `scale(${density})`,
            transformOrigin: 'top right',
            width: `${100 / density}vw`,
            height: `${100 / density}vh`,
          },
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          bgcolor: '#f8fafc',
        },
      }}
    >
      <Box sx={{
        flexShrink: 0,
        color: '#fff',
        px: { xs: 1.5, sm: 2 },
        py: 1.1,
        background: DISPATCH.headerGradient,
        borderBottom: '1px solid rgba(255,255,255,0.08)',
      }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Box sx={{
              width: 36,
              height: 36,
              borderRadius: 1.75,
              bgcolor: 'rgba(255,255,255,0.12)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 18,
            }}>{icon}</Box>
            <Box>
              <Typography variant="caption" sx={{ opacity: 0.7, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', fontSize: '0.68rem' }}>
                Dispatch Center
              </Typography>
              <Typography variant="subtitle1" fontWeight={800} letterSpacing="-0.02em" lineHeight={1.2} sx={{ fontSize: '1rem' }}>
                {title}
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Typography variant="caption" sx={{ opacity: 0.72, display: { xs: 'none', lg: 'block' }, maxWidth: 420 }} noWrap>
              {subtitle}
            </Typography>
            <IconButton onClick={onClose} size="small" sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.08)', '&:hover': { bgcolor: 'rgba(255,255,255,0.16)' } }}>
              <Close fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>
      </Box>

      <Box sx={{
        flex: 1,
        minHeight: 0,
        overflow: compact ? 'auto' : 'hidden',
        display: 'flex',
        flexDirection: 'column',
        px: { xs: 1, sm: 1.25 },
        py: 1,
        bgcolor: '#f1f5f9',
        WebkitOverflowScrolling: 'touch',
        ...bookFieldSx(accent),
      }}>
        {children}
      </Box>

      {onSubmit && (
        <Box sx={{
          px: { xs: 1.5, sm: 2 },
          py: 1,
          bgcolor: '#fff',
          borderTop: '1px solid #e2e8f0',
          flexShrink: 0,
          pb: 'calc(8px + env(safe-area-inset-bottom, 0px))',
        }}>
          <Stack direction={{ xs: 'column-reverse', sm: 'row' }} spacing={1} justifyContent="flex-end">
            <Button
              onClick={onClose}
              fullWidth={compact}
              variant="outlined"
              size="small"
              sx={{
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: '8px',
                borderColor: '#e2e8f0',
                color: '#475569',
                px: 2,
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={onSubmit}
              disabled={disableSubmit || loading}
              fullWidth={compact}
              size="small"
              sx={{
                fontWeight: 800,
                textTransform: 'none',
                borderRadius: '8px',
                px: 2.5,
                py: 0.85,
                minWidth: { sm: 180 },
                bgcolor: accent,
                boxShadow: 'none',
                '&:hover': { bgcolor: accent, filter: 'brightness(0.95)', boxShadow: 'none' },
              }}
            >
              {loading ? <CircularProgress size={18} color="inherit" /> : submitLabel}
            </Button>
          </Stack>
        </Box>
      )}
    </Drawer>
  );
}
